/**
 * Invite gate in front of `/#/pilot` — Oatmeal-composed.
 *
 * Replaces the old client-side password gate. The pilot is invite-only:
 * an admin provisions a `pilot_invites/{code}` doc and the invitee opens
 * `https://pilot.planapus.com/?invite=<code>`.
 *
 * Flow:
 *   1. On mount, read the invite code from the URL (`?invite=`) or, for a
 *      returning visitor, from localStorage.
 *   2. `validatePilotInvite(code)` against the backend — re-run on every
 *      load so a revoked invite stops working immediately.
 *   3. On success: cache the code, stash audience + identity into the
 *      localStorage keys `main.js` already reads, and render the pilot.
 *   4. Otherwise: show the "lost my link" resend form, which calls
 *      `requestPilotInviteByEmail` (anti-enumeration — always reports sent).
 */

import React, {useEffect, useState} from 'react';
import {useMutation} from '@apollo/client';
import {
  VALIDATE_PILOT_INVITE,
  REQUEST_PILOT_INVITE_BY_EMAIL,
} from '../../graphql/pilotAccessMutations';
import type {
  Mutation,
  MutationValidatePilotInviteArgs,
  MutationRequestPilotInviteByEmailArgs,
} from '../../types/generated/graphql';
import {Heading} from '../../oatmeal/elements/heading';
import {Eyebrow} from '../../oatmeal/elements/eyebrow';
import {Text} from '../../oatmeal/elements/text';
import {Button} from '../../oatmeal/elements/button';
import {dispatchPilotAuthChanged} from './PilotContext';

const CODE_KEY = 'pp-pilot-invite-code';
const AUDIENCE_KEY = 'mp-invite-audience';
const TALK_LEAD_KEY = 'mp-talk-lead';
const EMAIL_KEY = 'mp-email';
const ADMIN_EMAIL = 'admin@planapus.com';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  children: React.ReactNode;
}

function readInviteCodeFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const fromSearch = new URLSearchParams(window.location.search).get('invite');
  if (fromSearch && fromSearch.trim()) return fromSearch.trim();
  const hash = window.location.hash || '';
  const qIndex = hash.indexOf('?');
  if (qIndex >= 0) {
    const fromHash = new URLSearchParams(hash.slice(qIndex)).get('invite');
    if (fromHash && fromHash.trim()) return fromHash.trim();
  }
  return null;
}

// After successful validation, scrub the `invite` query from both the
// document URL and the HashRouter hash. The bare landing URL is
// `pilot.planapus.com/?invite=CODE`; HashRouter's RootRedirect carries
// `?invite=` into the hash too, so the visible URL ends up as
// `pilot.planapus.com/?invite=CODE#/pilot?invite=CODE` (code shown twice).
// Once validated, the code lives in localStorage — no reason to keep it
// in the URL where it churns history and looks broken.
function scrubInviteFromUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    let changed = false;
    if (url.searchParams.has('invite')) {
      url.searchParams.delete('invite');
      changed = true;
    }
    const hash = url.hash || '';
    const qIndex = hash.indexOf('?');
    if (qIndex >= 0) {
      const hashPath = hash.slice(0, qIndex);
      const hashParams = new URLSearchParams(hash.slice(qIndex));
      if (hashParams.has('invite')) {
        hashParams.delete('invite');
        const remaining = hashParams.toString();
        url.hash = remaining ? `${hashPath}?${remaining}` : hashPath;
        changed = true;
      }
    }
    if (changed) {
      window.history.replaceState({}, '', url.toString());
    }
  } catch {
    /* malformed URL — leave it alone */
  }
}

// Persist the validated invite's audience + identity into the localStorage
// keys main.js consumes. Best-effort — private mode may block localStorage.
function stashInviteContext(
  email?: string | null,
  name?: string | null,
  audience?: string | null,
): void {
  try {
    window.localStorage.setItem(
      AUDIENCE_KEY,
      audience === 'VC' ? 'VC' : 'CUSTOMER',
    );
    if (email || name) {
      const prev = JSON.parse(
        window.localStorage.getItem(TALK_LEAD_KEY) || '{}',
      );
      window.localStorage.setItem(
        TALK_LEAD_KEY,
        JSON.stringify({
          ...(prev && typeof prev === 'object' ? prev : {}),
          ...(name ? {name} : {}),
          ...(email ? {email} : {}),
        }),
      );
    }
    if (email) window.localStorage.setItem(EMAIL_KEY, email);
  } catch {
    /* private mode — fall through, the pilot still opens for the session */
  }
  // F9 — notify the live PilotProvider so a returning visitor's
  // audience change surfaces this tab without a reload.
  dispatchPilotAuthChanged();
}

export default function PilotInviteGate({children}: Props) {
  const [status, setStatus] = useState<'checking' | 'gated' | 'open'>(
    'checking',
  );
  const [emailInput, setEmailInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resendDone, setResendDone] = useState(false);

  const [validateInvite] = useMutation<
    Pick<Mutation, 'validatePilotInvite'>,
    MutationValidatePilotInviteArgs
  >(VALIDATE_PILOT_INVITE);
  const [requestInvite, {loading: requesting}] = useMutation<
    Pick<Mutation, 'requestPilotInviteByEmail'>,
    MutationRequestPilotInviteByEmailArgs
  >(REQUEST_PILOT_INVITE_BY_EMAIL);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setStatus('open');
      return;
    }
    let code = readInviteCodeFromUrl();
    if (!code) {
      try {
        code = window.localStorage.getItem(CODE_KEY);
      } catch {
        /* localStorage unavailable */
      }
    }
    if (!code) {
      setStatus('gated');
      return;
    }

    let cancelled = false;
    const inviteCode = code;
    validateInvite({variables: {code: inviteCode}})
      .then((res) => {
        if (cancelled) return;
        const result = res.data?.validatePilotInvite;
        if (result?.valid) {
          try {
            window.localStorage.setItem(CODE_KEY, inviteCode);
          } catch {
            /* private mode */
          }
          stashInviteContext(result.email, result.name, result.audience);
          scrubInviteFromUrl();
          setStatus('open');
        } else {
          try {
            window.localStorage.removeItem(CODE_KEY);
          } catch {
            /* private mode */
          }
          setStatus('gated');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('gated');
      });
    return () => {
      cancelled = true;
    };
  }, [validateInvite]);

  async function handleResend(e: React.FormEvent) {
    e.preventDefault();
    const email = emailInput.trim();
    if (!email) return;
    if (!EMAIL_RE.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    setError(null);
    try {
      await requestInvite({variables: {email}});
      setResendDone(true);
    } catch {
      setError("Couldn't send right now. Try again in a moment.");
    }
  }

  if (status === 'open') return <>{children}</>;

  return (
    <div className="oatmeal-root flex min-h-screen items-center justify-center bg-taupe-100 py-24 dark:bg-taupe-950">
      {/* Plain centered column — using <Container> here would inherit
          its lg:max-w-7xl + w-full and the inner max-w-md column would
          left-align inside the 80rem box at desktop widths (F-unauth-1
          from the audit). */}
      <div className="mx-auto flex w-full max-w-md flex-col gap-6 px-6">
        <Eyebrow>Planapus · Pilot</Eyebrow>

        {status === 'checking' ? (
          <>
            <Heading className="text-4xl/12 sm:text-5xl/14">
              Checking your invite…
            </Heading>
            <Text>One moment.</Text>
          </>
        ) : resendDone ? (
          <>
            <Heading className="text-4xl/12 sm:text-5xl/14">
              Check your inbox.
            </Heading>
            <Text>
              If that email has an active invite, the link is on its way.
              Open it on this device to continue.
            </Text>
          </>
        ) : (
          <>
            <Heading className="text-4xl/12 sm:text-5xl/14">
              Invite-only preview.
            </Heading>
            <Text>
              If you have an invite link, open it to continue. Lost it? Enter
              the email your invite was sent to and we&apos;ll resend the
              link.
            </Text>
            <form
              onSubmit={handleResend}
              className="flex flex-col gap-3"
              aria-label="Resend pilot invite"
            >
              <label
                htmlFor="pp-pilot-invite-email"
                className="font-mono text-xs/5 uppercase tracking-widest text-taupe-700 dark:text-taupe-400"
              >
                Work email
              </label>
              <input
                id="pp-pilot-invite-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  if (error) setError(null);
                }}
                autoFocus
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={error ? 'pp-pilot-invite-err' : undefined}
                className="rounded-lg border border-taupe-300 bg-white px-3 py-2.5 text-sm text-taupe-950 placeholder:text-taupe-500 focus:border-taupe-950 focus:outline-none focus:ring-2 focus:ring-taupe-300 dark:border-taupe-700 dark:bg-taupe-900 dark:text-white"
              />
              {error && (
                <p
                  id="pp-pilot-invite-err"
                  role="alert"
                  className="text-xs/5 text-red-700"
                >
                  {error}
                </p>
              )}
              <Button
                type="submit"
                size="lg"
                disabled={requesting || !emailInput.trim()}
                className="disabled:cursor-default disabled:opacity-50"
              >
                {requesting ? 'Sending…' : 'Email me the link'}
              </Button>
            </form>
          </>
        )}

        <Text className="text-xs/5 text-taupe-600 dark:text-taupe-500">
          Not invited yet? Reach out at{' '}
          <a
            href={`mailto:${ADMIN_EMAIL}`}
            className="font-medium text-taupe-950 underline underline-offset-4 dark:text-white"
          >
            {ADMIN_EMAIL}
          </a>
          .
        </Text>
      </div>
    </div>
  );
}
